// side-menu.component.ts
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MenuController } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  MenuItem,
  SideMenuService,
} from '../../services/side-menu/side-menu.service';
import { MessageService } from '../../services/message/message.service';
import { HomeComponent } from 'src/app/core/components/home/home.component';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  standalone: false,
})
export class SideMenuComponent implements OnInit {
  @Input() public menuState: 'collapsed' | 'expanded' = 'expanded';

  public menuItems: MenuItem[] = [];
  public selectedMenuItemIndex = -1;

  constructor(
    private sideMenuService: SideMenuService,
    private menuController: MenuController,
    private router: Router,
    private messageService: MessageService
  ) {}

  async ngOnInit() {
    await this.loadMenuItems();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.updateActiveItem(e.urlAfterRedirects);
      });
  }

  private async loadMenuItems() {
    this.sideMenuService.getMenuItems().subscribe({
      next: (res) => {
        this.menuItems = res.map(item => ({
          ...item,
          name: this.formatMenuName(item.name),
        }));
        this.updateActiveItem(this.router.url);
      },
    });
  }

  private updateActiveItem(url: string) {
    const cleanedUrl = url.split(/[?#]/)[0];

    // ra-catalog → captain-orders
    if (cleanedUrl.startsWith('/resto-admin/ra-catalog')) {
      const captainIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/resto-admin/captain-orders'
      );
      this.selectedMenuItemIndex = captainIndex;
      return;
    }

    // table-booking/view → table-booking
    if (cleanedUrl.startsWith('/resto-admin/table-booking/view')) {
      const tableBookingIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/resto-admin/table-booking'
      );
      this.selectedMenuItemIndex = tableBookingIndex;
      return;
    }

    // menus/{id} → menus
    if (cleanedUrl.startsWith('/resto-admin/menus/')) {
      const menusIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/resto-admin/menus'
      );
      this.selectedMenuItemIndex = menusIndex;
      return;
    }

    if (cleanedUrl.startsWith('/resto-admin/offers/map-offer')) {
      const offerIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/resto-admin/offers'
      );
      this.selectedMenuItemIndex = offerIndex;
      return;
    }
    if (cleanedUrl.startsWith('/customer/profile')) {
      const profileIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/customer/profile'
      );
      this.selectedMenuItemIndex = profileIndex;
      return;
    }

    if (cleanedUrl.startsWith('/admin/branches')) {
      const branchIndex = this.menuItems.findIndex(
        (mi) => mi.link === '/admin/branches/'
      );

      this.selectedMenuItemIndex = branchIndex;
      return;
    }

    // Default: exact match
    const idx = this.menuItems?.findIndex((mi) => mi.link === cleanedUrl);
    this.selectedMenuItemIndex = idx;
  }

  public async onItemClick(event: MouseEvent, item: MenuItem, index: number) {
    this.selectedMenuItemIndex = index;
    this.menuController.close(HomeComponent.sideMenuId);
    await this.router.navigate([item.link]);
  }

  public displayNotSubscribedMessage(feature: string) {
    this.messageService.showMessage(
      `You have not subscribed to ${feature}. Please contact Administrator`
    );
  }

  public formatMenuName(name: string): string {
    if (!name) return '';

    if (name.toUpperCase() === 'AI_FEATURES') {
      return 'AI Features';
    }

    return name
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
